/** AG Grid v36 DOM selectors (v35 viewport/container class names were removed). */

/** Native vertical/horizontal scroll element in v36. */
export const AG_GRID_SCROLL_VIEWPORT = '.ag-grid-viewport';
/** Content wrapper inside the viewport; holds all body rows. */
export const AG_GRID_SCROLLING_CONTAINER = '.ag-grid-scrolling-container';
/** Flex row wrapper used by pinned top/bottom row sections. */
export const AG_GRID_SCROLLING_ROWS = '.ag-grid-scrolling-rows';
/** Center cells section inside each data/header row (not the all-rows container). */
export const AG_GRID_ROW_CENTER_CELLS = '.ag-grid-scrolling-cells';
/** @deprecated Use AG_GRID_SCROLLING_CONTAINER for the rows container. */
export const AG_GRID_CENTER_ROWS = AG_GRID_SCROLLING_CONTAINER;
export const AG_GRID_PINNED_LEFT_HEADER = '.ag-header .ag-grid-pinned-left-cells';
export const AG_GRID_PINNED_RIGHT_HEADER = '.ag-header .ag-grid-pinned-right-cells';
export const AG_GRID_PINNED_LEFT_BODY_ROWS = '.ag-grid-scrolling-rows .ag-grid-pinned-left-cells';
export const AG_GRID_PINNED_RIGHT_BODY_ROWS = '.ag-grid-scrolling-rows .ag-grid-pinned-right-cells';
export const AG_GRID_PINNED_TOP_ROWS = '.ag-grid-pinned-top-rows';
export const AG_GRID_PINNED_TOP_ROWS_CONTAINER = '.ag-grid-pinned-top-rows-container';

/**
 * True when at least one body row has rendered cells (not an empty infinite-model placeholder).
 * @param {ParentNode | null | undefined} root
 * @returns {boolean}
 */
export function hasPaintedGridBodyRows(root) {
    if (!root) {
        return false;
    }

    const rows = root.querySelectorAll(
        `${AG_GRID_SCROLLING_CONTAINER} > .ag-row:not(.ag-row-loading):not(.w5-row-loading)`,
    );
    return Array.from(rows).some((row) => row.querySelector('.ag-cell') != null);
}

/**
 * Waits until body rows exist in the DOM or hardMaxFrames is reached.
 * @param {{ root?: ParentNode | null, onReady: () => void, maxFrames?: number, hardMaxFrames?: number }} options
 * @returns {() => void} cancel function
 */
export function waitForGridBodyRowsPainted({
    root,
    onReady,
    maxFrames = 15,
    hardMaxFrames = maxFrames * 4,
}) {
    if (!onReady) {
        return () => {};
    }

    if (hasPaintedGridBodyRows(root)) {
        onReady();
        return () => {};
    }

    let cancelled = false;
    let frames = 0;

    const tick = () => {
        if (cancelled) {
            return;
        }

        frames += 1;

        if (hasPaintedGridBodyRows(root) || frames >= hardMaxFrames) {
            onReady();
            return;
        }

        requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);

    return () => {
        cancelled = true;
    };
}

/** Marks the DataGrid outer root used for floating row-actions positioning. */
export const DATA_GRID_ROOT_ATTR = 'data-w5-datagrid-root';

/**
 * Resolves the DataGrid root element used to position the floating actions overlay.
 * Prefers `tableId` when present; otherwise walks up from a grid cell.
 * @param {{ tableId?: string, eGridCell?: Element | null }} [options]
 * @returns {HTMLElement | null}
 */
export function resolveDataGridRoot({ tableId, eGridCell } = {}) {
    if (tableId) {
        const byId = document.getElementById(tableId);
        if (byId) return byId;
    }

    return eGridCell?.closest?.(`[${DATA_GRID_ROOT_ATTR}]`) ?? null;
}

/**
 * @param {ParentNode | null | undefined} root
 * @returns {HTMLElement | null}
 */
export function queryGridScrollViewport(root) {
    return root?.querySelector?.(AG_GRID_SCROLL_VIEWPORT) ?? null;
}

/**
 * @param {ParentNode | null | undefined} root
 * @returns {HTMLElement | null}
 */
export function queryGridScrollingRows(root) {
    return root?.querySelector?.(AG_GRID_SCROLLING_ROWS) ?? null;
}

/**
 * @param {ParentNode} [root=document]
 * @returns {HTMLElement | null}
 */
export function queryGridCenterRowsContainer(root = document) {
    return root.querySelector?.(AG_GRID_SCROLLING_CONTAINER) ?? null;
}
